// Wave 20.47c — public replay view, redesigned.
//
// Route: /replay/:token. Anonymous or logged-in.
//
// - Full-bleed map with 3D terrain + wind particle overlay.
// - Camera fits the whole polyline ONCE; only the marker animates.
// - Auto-play at the same speed the in-app fullscreen replay uses
//   (computeAutoSpeed from SessionReplayControls) — a 30 min ride
//   plays in ~15 s.
// - Glassy HUD chip strip up top.
// - Bottom pill dock: play/pause + scrub + branded cheer bar +
//   comment CTA (when the owner allowed comments).
// - Comment pins as position-anchored ticks on the scrub bar.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReportIcon from '@mui/icons-material/OutlinedFlag';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import CommentIcon from '@mui/icons-material/ChatBubbleOutline';
import { useParams } from 'react-router-dom';
import RouteMap from './components/RouteMap';
import CheerLayer, { type CheerLayerHandle } from './components/CheerLayer';
import CheerBar from './components/CheerBar';
import Logo from './Logo';
import { colors } from './theme';
import { computeAutoSpeed } from './components/SessionReplayControls';
import {
  fmtDistance as fmtDistanceUnits,
  fmtElevation as fmtElevationUnits,
  fmtPace as fmtPaceUnits,
  fmtSpeed as fmtSpeedUnits,
  fmtTemp,
  useUnitSystem,
} from './services/useUnitSystem';

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';
const NAME_KEY = 'aero.chat_replay_viewer_name';

interface ReplayPayload {
  share: {
    token: string;
    commentsEnabled: boolean;
    createdAt: string;
    expiresAt: string | null;
  };
  owner: { id: number; name: string; handle: string | null } | null;
  session: {
    id: string;
    type: string;
    startedAt: string | null;
    endedAt: string | null;
    distanceM: number | null;
    elevationGainM: number | null;
    avgSpeedMps: number | null;
    maxSpeedMps: number | null;
    title: string | null;
    description: string | null;
    gpsTrack: Array<{ t: number; lat: number; lng: number; alt?: number; speed?: number }> | null;
    timeSeries: Array<{ t: number; power_w?: number | null; hr_bpm?: number | null }> | null;
    weatherSummary: any;
  };
  cheers: Array<{ id: string; emoji: string; positionSec: number; sender: string; createdAt: string }>;
  comments: Array<{ id: string; author: string; positionSec: number; text: string; createdAt: string }>;
}

function optionalAuth(): Record<string, string> {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}
function getStoredName(): string {
  try {
    return localStorage.getItem(NAME_KEY) ?? '';
  } catch {
    return '';
  }
}
function setStoredName(name: string) {
  try {
    localStorage.setItem(NAME_KEY, name);
  } catch {}
}

export default function ReplayView() {
  const { token = '' } = useParams<{ token: string }>();
  const units = useUnitSystem();
  const [data, setData] = useState<ReplayPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [snack, setSnack] = useState<string | null>(null);

  // ---------- playback clock ----------
  const [playing, setPlaying] = useState(true);
  const [posSec, setPosSec] = useState(0);
  const durationSec = useMemo(() => {
    const g = data?.session.gpsTrack;
    if (!g || g.length < 2) return 0;
    return Math.max(0, g[g.length - 1].t - g[0].t);
  }, [data]);
  const autoSpeed = useMemo(() => computeAutoSpeed(durationSec), [durationSec]);
  useEffect(() => {
    if (!playing || durationSec <= 0) return;
    const start = performance.now();
    const initial = posSec;
    let raf = 0;
    const tick = () => {
      const elapsed = (performance.now() - start) / 1000;
      const next = initial + elapsed * autoSpeed;
      if (next >= durationSec) {
        setPosSec(durationSec);
        setPlaying(false);
        return;
      }
      setPosSec(next);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, durationSec, autoSpeed]);

  // ---------- sample interpolation ----------
  const currentSample = useMemo(() => {
    const g = data?.session.gpsTrack;
    if (!g || g.length === 0) return null;
    const target = g[0].t + posSec;
    if (target <= g[0].t) return g[0];
    if (target >= g[g.length - 1].t) return g[g.length - 1];
    let lo = 0;
    let hi = g.length - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (g[mid].t <= target) lo = mid;
      else hi = mid;
    }
    const a = g[lo];
    const b = g[hi];
    const alpha = (target - a.t) / Math.max(1e-6, b.t - a.t);
    return {
      t: target,
      lat: a.lat + (b.lat - a.lat) * alpha,
      lng: a.lng + (b.lng - a.lng) * alpha,
      alt: (a.alt ?? 0) + ((b.alt ?? 0) - (a.alt ?? 0)) * alpha,
      speed: (a.speed ?? 0) + ((b.speed ?? 0) - (a.speed ?? 0)) * alpha,
    };
  }, [data, posSec]);

  const currentSensor = useMemo(() => {
    const ts = data?.session.timeSeries;
    if (!ts || ts.length === 0) return null;
    let lo = 0;
    let hi = ts.length - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (ts[mid].t <= posSec) lo = mid;
      else hi = mid;
    }
    return ts[lo];
  }, [data, posSec]);

  const cumDist = useMemo(() => {
    const g = data?.session.gpsTrack;
    if (!g || g.length < 2) return 0;
    const target = g[0].t + posSec;
    let sum = 0;
    for (let i = 1; i < g.length; i++) {
      const a = g[i - 1];
      const b = g[i];
      if (a.t >= target) break;
      const nextT = Math.min(target, b.t);
      const R = 6371000;
      const dLat = ((b.lat - a.lat) * Math.PI) / 180;
      const dLng = ((b.lng - a.lng) * Math.PI) / 180;
      const s1 = Math.sin(dLat / 2);
      const s2 = Math.sin(dLng / 2);
      const q =
        s1 * s1 +
        Math.cos((a.lat * Math.PI) / 180) *
          Math.cos((b.lat * Math.PI) / 180) *
          s2 * s2;
      const d = 2 * R * Math.asin(Math.min(1, Math.sqrt(q)));
      const frac = (nextT - a.t) / Math.max(1e-6, b.t - a.t);
      sum += d * Math.max(0, Math.min(1, frac));
    }
    return sum;
  }, [data, posSec]);

  // ---------- stable RouteMap props (kills flash) ----------
  const stableRoutePoints = useMemo(
    () =>
      data?.session.gpsTrack?.map((p) => ({
        lat: p.lat,
        lng: p.lng,
        t: p.t,
      })) ?? [],
    [data?.session.gpsTrack],
  );
  const stablePlaybackMarker = useMemo(
    () => (currentSample ? { lat: currentSample.lat, lng: currentSample.lng } : null),
    [currentSample?.lat, currentSample?.lng],
  );
  const stableWindReplay = useMemo(
    () =>
      data?.session.startedAt && data?.session.endedAt
        ? { startedAt: data.session.startedAt, endedAt: data.session.endedAt }
        : undefined,
    [data?.session.startedAt, data?.session.endedAt],
  );

  const cheerLayerRef = useRef<CheerLayerHandle | null>(null);

  // Load + poll for new cheers/comments.
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await fetch(`${API}/public/replay/${token}`, { headers: optionalAuth() });
        if (!alive) return;
        if (res.status === 404) {
          setError('This replay drop is no longer available.');
          return;
        }
        if (!res.ok) throw new Error(`Load failed (${res.status})`);
        const j = await res.json();
        if (!alive) return;
        setData(j);
      } catch (e: any) {
        if (!alive) return;
        setError(e.message || 'Could not load this replay.');
      }
    };
    void load();
    const id = window.setInterval(load, 15000);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [token]);

  // Auto-fire cheers persisted before we joined for visual life.
  useEffect(() => {
    if (!data || !cheerLayerRef.current) return;
    const layer = cheerLayerRef.current;
    // Spawn one for every existing cheer, staggered.
    data.cheers.slice(-8).forEach((c, i) => {
      window.setTimeout(() => {
        const rect = document.getElementById('replay-map-root')?.getBoundingClientRect();
        if (!rect) return;
        layer.spawn({
          emojiKey: c.emoji,
          x: rect.width * (0.15 + Math.random() * 0.7),
          y: rect.height * (0.35 + Math.random() * 0.45),
        });
      }, 400 + i * 220);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.share.token]);

  // ---------- name capture + comment composer ----------
  const [nameOpen, setNameOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<null | (() => void)>(null);
  const [nameInput, setNameInput] = useState('');
  const [commentOpen, setCommentOpen] = useState(false);
  const [commentPosition, setCommentPosition] = useState<number>(0);
  const [commentText, setCommentText] = useState('');

  const needsName = () => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (t) return false;
    if (getStoredName()) return false;
    return true;
  };
  const withName = (fn: () => void) => {
    if (!needsName()) {
      fn();
      return;
    }
    setPendingAction(() => fn);
    setNameInput('');
    setNameOpen(true);
  };
  const confirmName = () => {
    const s = nameInput.trim().slice(0, 64);
    if (!s) return;
    setStoredName(s);
    setNameOpen(false);
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  };

  const sendCheer = (emojiKey: string) => {
    if (!data) return;
    withName(async () => {
      const rect = document.getElementById('replay-map-root')?.getBoundingClientRect();
      if (rect && cheerLayerRef.current) {
        cheerLayerRef.current.spawn({
          emojiKey,
          x: rect.width * (0.35 + Math.random() * 0.3),
          y: rect.height * (0.6 + Math.random() * 0.2),
        });
      }
      try {
        await fetch(`${API}/public/replay/${token}/cheer`, {
          method: 'POST',
          headers: { ...optionalAuth(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            emojiKey,
            positionSec: Math.round(posSec),
            displayName: getStoredName() || undefined,
          }),
        });
      } catch {}
    });
  };

  const openComment = () => {
    if (!data?.share.commentsEnabled) return;
    setCommentPosition(Math.round(posSec));
    setCommentText('');
    setCommentOpen(true);
  };
  const submitComment = () => {
    if (!commentText.trim()) return;
    const text = commentText.trim().slice(0, 500);
    const pos = commentPosition;
    setCommentOpen(false);
    withName(async () => {
      try {
        const res = await fetch(`${API}/public/replay/${token}/comment`, {
          method: 'POST',
          headers: { ...optionalAuth(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            text,
            positionSec: pos,
            displayName: getStoredName() || undefined,
          }),
        });
        if (!res.ok) throw new Error('comment failed');
        const c = await res.json();
        setData((cur) => (cur ? { ...cur, comments: [...cur.comments, c] } : cur));
      } catch {
        setSnack('Could not post your comment — try again in a moment.');
      }
    });
  };

  const reportComment = async (id: string) => {
    try {
      await fetch(`${API}/public/replay/comments/${id}/report`, {
        method: 'POST',
        headers: { ...optionalAuth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'other' }),
      });
      setSnack('Reported. Thanks for the flag.');
    } catch {}
  };
  const deleteComment = async (id: string) => {
    try {
      await fetch(`${API}/replay-comments/${id}`, {
        method: 'DELETE',
        headers: optionalAuth(),
      });
      setData((cur) => (cur ? { ...cur, comments: cur.comments.filter((c) => c.id !== id) } : cur));
    } catch {}
  };

  const isOwner = useMemo(() => {
    try {
      const t = localStorage.getItem(TOKEN_KEY);
      if (!t || !data?.owner) return false;
      const payload = JSON.parse(atob(t.split('.')[1] ?? ''));
      return payload?.sub && Number(payload.sub) === data.owner.id;
    } catch {
      return false;
    }
  }, [data]);

  // Wave 20.47d — expiration countdown. Ticks every second so the
  // rider watching sees the drop expire in real time. When the
  // share never expires, we render "Never expires" instead.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!data?.share.expiresAt) return;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [data?.share.expiresAt]);
  const expiresLabel = useMemo(() => {
    if (!data?.share.expiresAt) return 'Never expires';
    const remainMs = new Date(data.share.expiresAt).getTime() - nowMs;
    if (remainMs <= 0) return 'Expired';
    return `Expires in ${fmtCountdown(remainMs)}`;
  }, [data?.share.expiresAt, nowMs]);

  const [selectedComment, setSelectedComment] = useState<string | null>(null);
  const commentAtPos = useMemo(() => {
    if (!data?.share.commentsEnabled) return null;
    // Which comment is close to the current playback position (±3 s).
    return data.comments.find((c) => Math.abs(c.positionSec - posSec) < 3) ?? null;
  }, [data, posSec]);

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Alert severity="warning">{error}</Alert>
      </Box>
    );
  }
  if (!data) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const isRun = data.session.type === 'outdoor_running';
  const wx = data.session.weatherSummary;

  return (
    <Box sx={{ position: 'fixed', inset: 0, backgroundColor: colors.bg, display: 'flex', flexDirection: 'column' }}>
      {/* Full-bleed map area */}
      <Box id="replay-map-root" sx={{ flex: 1, position: 'relative', minHeight: 0 }}>
        {stableRoutePoints.length >= 2 && (
          <RouteMap
            points={stableRoutePoints}
            terrain3D
            terrainExaggeration={3}
            windReplay={stableWindReplay}
            playbackMarker={stablePlaybackMarker}
            height="100%"
          />
        )}

        <CheerLayer ref={cheerLayerRef} />

        {/* Top row: aero.chat logo (→ /register) + title/owner + close */}
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            right: 12,
            display: 'flex',
            alignItems: 'stretch',
            gap: 1,
            zIndex: 15,
            pointerEvents: 'none',
          }}
        >
          <Box
            component="a"
            href="/register"
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 1.5,
              py: 0.75,
              borderRadius: 2,
              backgroundColor: 'rgba(10,14,26,0.82)',
              border: `1px solid rgba(255,255,255,0.1)`,
              backdropFilter: 'blur(10px)',
              pointerEvents: 'auto',
              textDecoration: 'none',
              '&:hover': { backgroundColor: 'rgba(30,30,38,0.9)' },
            }}
            aria-label="Go to aero.chat"
          >
            <Logo width={44} height={26} />
          </Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              px: 1.5,
              py: 0.75,
              borderRadius: 2,
              backgroundColor: 'rgba(10,14,26,0.72)',
              border: `1px solid rgba(255,255,255,0.1)`,
              backdropFilter: 'blur(10px)',
              pointerEvents: 'auto',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Typography sx={{ fontWeight: 800, color: colors.text, fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {data.session.title || (isRun ? 'A run on aero.chat' : 'A ride on aero.chat')}
            </Typography>
            {data.owner && (
              <Typography sx={{ color: colors.textMuted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                by {data.owner.name}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={() => {
              if (window.history.length > 1) window.history.back();
              else window.location.assign('/');
            }}
            sx={{
              color: colors.text,
              backgroundColor: 'rgba(10,14,26,0.72)',
              border: `1px solid rgba(255,255,255,0.1)`,
              backdropFilter: 'blur(10px)',
              pointerEvents: 'auto',
              alignSelf: 'flex-start',
              '&:hover': { backgroundColor: 'rgba(30,30,38,0.85)' },
            }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Countdown + Join CTA — floats bottom-left of the map on top
            of the description, so anonymous viewers always see both
            the ticking expiration and a path into aero.chat. */}
        <Box
          sx={{
            position: 'absolute',
            top: 76,
            left: 12,
            zIndex: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.75,
            borderRadius: 999,
            backgroundColor: 'rgba(10,14,26,0.82)',
            border: `1px solid rgba(255,255,255,0.1)`,
            backdropFilter: 'blur(10px)',
          }}
        >
          <Typography sx={{ color: colors.textMuted, fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.04em' }}>
            {expiresLabel}
          </Typography>
          <Box sx={{ width: 1, height: 14, backgroundColor: 'rgba(255,255,255,0.15)' }} />
          <Box
            component="a"
            href="/register"
            sx={{
              color: colors.brand,
              fontSize: '0.8rem',
              fontWeight: 800,
              textDecoration: 'none',
              letterSpacing: '0.03em',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Join aero.chat →
          </Box>
        </Box>

        {/* HUD chip strip, top-center under the header + countdown */}
        <Box
          sx={{
            position: 'absolute',
            top: 76,
            left: '50%',
            transform: 'translateX(-50%)',
            maxWidth: 'calc(100% - 340px)',
            display: 'flex',
            gap: 0.5,
            px: 0.75,
            py: 0.75,
            borderRadius: 999,
            backgroundColor: 'rgba(10,14,26,0.82)',
            border: `1px solid rgba(255,255,255,0.1)`,
            backdropFilter: 'blur(14px)',
            zIndex: 10,
            overflowX: 'auto',
            flexWrap: 'nowrap',
          }}
        >
          <HudChip label="TIME" value={fmtClock(posSec)} />
          {data.session.distanceM != null && (
            <HudChip label="DIST" value={fmtDistanceUnits(cumDist, units)} />
          )}
          {currentSample && (
            <HudChip
              label={isRun ? 'PACE' : 'SPEED'}
              value={isRun ? fmtPaceUnits(currentSample.speed, units) : fmtSpeedUnits(currentSample.speed, units)}
            />
          )}
          {currentSample && (
            <HudChip label="ALT" value={fmtElevationUnits(currentSample.alt, units)} />
          )}
          {currentSensor?.power_w != null && (
            <HudChip label="POWER" value={`${Math.round(currentSensor.power_w)} W`} />
          )}
          {currentSensor?.hr_bpm != null && (
            <HudChip label="HR" value={`${Math.round(currentSensor.hr_bpm)} bpm`} />
          )}
          {wx?.temperature_c?.avg != null && (
            <HudChip label="TEMP" value={fmtTemp(wx.temperature_c.avg, units)} />
          )}
          {wx?.wind_speed_ms?.avg != null && wx.wind_speed_ms.avg > 0 && (
            <HudChip label="WIND" value={fmtSpeedUnits(wx.wind_speed_ms.avg, units)} />
          )}
        </Box>

        {/* Description card (bottom-left) */}
        {data.session.description && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 130,
              left: 12,
              maxWidth: 400,
              p: 1.5,
              borderRadius: 2,
              backgroundColor: 'rgba(10,14,26,0.75)',
              border: `1px solid rgba(255,255,255,0.1)`,
              backdropFilter: 'blur(8px)',
              zIndex: 8,
            }}
          >
            <Typography sx={{ color: colors.text, fontSize: '0.85rem', whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
              {data.session.description}
            </Typography>
          </Box>
        )}

        {/* Popover for the comment currently under the playhead */}
        {commentAtPos && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 130,
              right: 12,
              maxWidth: 340,
              p: 1.5,
              borderRadius: 2,
              backgroundColor: 'rgba(10,14,26,0.9)',
              border: `1px solid ${colors.brand}`,
              backdropFilter: 'blur(8px)',
              zIndex: 12,
              boxShadow: `0 0 24px rgba(106,255,69,0.25)`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <Typography sx={{ color: colors.brand, fontWeight: 800, fontSize: '0.7rem', letterSpacing: '0.1em' }}>
                {fmtClock(commentAtPos.positionSec)}
              </Typography>
              <Typography sx={{ color: colors.text, fontWeight: 700, fontSize: '0.85rem' }}>{commentAtPos.author}</Typography>
              <Box sx={{ flex: 1 }} />
              {isOwner ? (
                <IconButton size="small" onClick={() => deleteComment(commentAtPos.id)} sx={{ color: colors.textMuted, p: 0.25 }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              ) : (
                <IconButton size="small" onClick={() => reportComment(commentAtPos.id)} sx={{ color: colors.textMuted, p: 0.25 }}>
                  <ReportIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
            <Typography sx={{ color: colors.text, fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>{commentAtPos.text}</Typography>
          </Box>
        )}
      </Box>

      {/* Bottom pill dock: play/pause + scrub + cheers + comment */}
      <Box
        sx={{
          px: 2,
          pb: 2,
          pt: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
          alignItems: 'center',
          background: 'linear-gradient(to top, rgba(10,14,26,0.95), rgba(10,14,26,0))',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', maxWidth: 720 }}>
          <IconButton
            onClick={() => setPlaying((p) => !p)}
            sx={{
              color: '#0a100c',
              backgroundColor: colors.brand,
              width: 44,
              height: 44,
              '&:hover': { backgroundColor: colors.brand, opacity: 0.9 },
            }}
          >
            {playing ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
          <Box sx={{ flex: 1, position: 'relative', height: 32, display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{ width: '100%', height: 6, borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.15)', position: 'relative', cursor: 'pointer' }}
              onClick={(e) => {
                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
                setPosSec(frac * durationSec);
              }}
            >
              <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${(posSec / Math.max(1, durationSec)) * 100}%`, backgroundColor: colors.brand, borderRadius: 3 }} />
              {/* Comment ticks */}
              {data.share.commentsEnabled && data.comments.map((c) => {
                const frac = c.positionSec / Math.max(1, durationSec);
                return (
                  <Tooltip key={c.id} title={`${c.author}: ${c.text}`} placement="top" arrow>
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        setPosSec(c.positionSec);
                        setPlaying(false);
                        setSelectedComment(c.id);
                      }}
                      sx={{
                        position: 'absolute',
                        left: `calc(${frac * 100}% - 5px)`,
                        top: -3,
                        width: 10,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: colors.data ?? '#00d9ff',
                        border: `2px solid #0a100c`,
                        cursor: 'pointer',
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
          <Typography sx={{ color: colors.textMuted, fontSize: '0.75rem', fontFamily: 'Menlo', minWidth: 100, textAlign: 'right' }}>
            {fmtClock(posSec)} / {fmtClock(durationSec)}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" justifyContent="center">
          <CheerBar onCheer={sendCheer} />
          {data.share.commentsEnabled && (
            <Button
              size="small"
              variant="outlined"
              onClick={openComment}
              startIcon={<CommentIcon fontSize="small" />}
              sx={{
                color: colors.brand,
                borderColor: 'rgba(106,255,69,0.5)',
                fontWeight: 700,
                textTransform: 'none',
                '&:hover': { borderColor: colors.brand, backgroundColor: 'rgba(106,255,69,0.1)' },
              }}
            >
              Comment at {fmtClock(posSec)}
            </Button>
          )}
        </Stack>
      </Box>

      {/* Name modal */}
      <Dialog open={nameOpen} onClose={() => setNameOpen(false)}>
        <DialogTitle>What's your name?</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value.slice(0, 64))}
            placeholder="Your name"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNameOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={confirmName}
            disabled={!nameInput.trim()}
            sx={{ backgroundColor: colors.brand, color: '#0a100c' }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Comment composer */}
      <Dialog open={commentOpen} onClose={() => setCommentOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Comment at {fmtClock(commentPosition)}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            value={commentText}
            onChange={(e) => setCommentText(e.target.value.slice(0, 500))}
            placeholder="Leave a note pinned to this moment in the ride."
            inputProps={{ maxLength: 500 }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitComment}
            disabled={!commentText.trim()}
            sx={{ backgroundColor: colors.brand, color: '#0a100c' }}
          >
            Post
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={2500} onClose={() => setSnack(null)} message={snack ?? ''} />
    </Box>
  );
}

function HudChip({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ minWidth: 68, px: 1.25, py: 0.5, borderRadius: 999, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Typography sx={{ color: colors.brand, fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.15em', lineHeight: 1 }}>{label}</Typography>
      <Typography sx={{ color: colors.text, fontSize: '0.95rem', fontWeight: 800, fontFamily: 'Menlo', lineHeight: 1.2, mt: 0.25 }}>{value}</Typography>
    </Box>
  );
}

function fmtClock(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  return `${m}:${String(ss).padStart(2, '0')}`;
}

// Wave 20.47d — coarse-then-fine expiration display: shows days
// until <1d, hours until <1h, then MM:SS in the last hour.
function fmtCountdown(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s >= 24 * 3600) {
    const d = Math.floor(s / (24 * 3600));
    const h = Math.floor((s % (24 * 3600)) / 3600);
    return `${d}d ${h}h`;
  }
  if (s >= 3600) {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return `${h}h ${m}m`;
  }
  const m = Math.floor(s / 60);
  const ss = s % 60;
  return `${m}:${String(ss).padStart(2, '0')}`;
}
